import React, { createContext, ReactNode, useContext, useEffect, useReducer, useRef } from 'react';

import { useAppConfiguration } from '~providers/AppConfigurationProvider';
import { useAssignedDiallerGroup } from '~providers/AssignedDiallerGroupProvider';
import { fetchTimezones } from '~providers/AsteriskDiallerProvider/api';
import {
  DiallerAction,
  diallerReducer,
  DiallerState,
  initialDiallerState,
} from '~providers/AsteriskDiallerProvider/reducer';
import { useAuth } from '~providers/AuthProvider';
import AsteriskDiallerManager from '~services/AsteriskDiallerManager';
import { AgentState, ServerMessage } from '~services/AsteriskDiallerManager/domain';
import { isEmpty } from '~utils/Functions';

type Props = {
  state: DiallerState;
  dispatch: React.Dispatch<DiallerAction>;
  socketRef: React.RefObject<AsteriskDiallerManager | null>;
  manualOutboundEnabled: boolean;
  allowAgentLeaveCall: boolean;
  canPauseCallRecordings: boolean;
  hasAmplifonSipFormFields: boolean;
  hasHCFSipFormFields: boolean;
  loadTimezones: () => void;
};

const AsteriskDiallerContext = createContext<Props | undefined>(undefined);

export const useAsteriskDialler = (): Props => {
  return useContext(AsteriskDiallerContext) as Props;
};

const AsteriskDiallerProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(diallerReducer, initialDiallerState);
  const socketRef = useRef<AsteriskDiallerManager | null>(null);
  const agentStateRef = useRef<string>('');
  const contactIdRef = useRef<string>('');
  const appConfig = useAppConfiguration();
  const assignedGroup = useAssignedDiallerGroup();
  const { fullName } = useAuth();

  const predictiveConfig = appConfig.extensions.predictive ?? {
    defaultTrunk: '',
    diallerURL: '',
    sipWebsocket: '',
    sipHost: '',
  };

  const loadTimezones = async () => {
    if (state.timezoneList.length > 0) return;
    const list = await fetchTimezones();
    dispatch({ type: 'SET_TIMEZONE_LIST', payload: list });
  };

  useEffect(() => {
    // Connect to Asterisk Dialler Manager WebSocket
    const connection = new AsteriskDiallerManager(
      predictiveConfig.diallerURL + '/agent-ws',
      assignedGroup.configuredGroup?.diallerGroupId || assignedGroup.group.diallerGroupId,
      fullName,
      predictiveConfig.sipWebsocket,
      predictiveConfig.sipHost,
    );
    connection.connect();
    socketRef.current = connection;

    connection.on('open', () => {
      dispatch({ type: 'SET_CONNECTED', payload: true });
    });

    connection.on('message', (data: ServerMessage) => {
      if (isEmpty(data)) return;

      if (data.campaignInformation) {
        dispatch({
          type: 'SET_CAMPAIGN_INFORMATION',
          payload: data.campaignInformation,
        });
      }
      if (data.enteredState) {
        dispatch({ type: 'SET_ENTERED_STATE', payload: data.enteredState });
        agentStateRef.current = data.enteredState.newState;

        if (data.enteredState.newState === AgentState.Connected && contactIdRef.current) {
          connection.startMetricsPolling(contactIdRef.current);
        }
        if (data.enteredState.newState === AgentState.AfterCallWork) {
          connection.stopMetricsPolling();
          contactIdRef.current = '';
        }
      }
      if (data.pendingState) {
        dispatch({ type: 'SET_PENDING_STATE', payload: data.pendingState });
      }
      if (data.callInformation) {
        dispatch({ type: 'SET_CALL_INFORMATION', payload: data.callInformation });
        if (data.callInformation.exclusionLists.length > 0) {
          dispatch({ type: 'SET_EXCLUSION_LISTS', payload: data.callInformation.exclusionLists });
        }

        // Track contactId for metrics polling (may arrive before or after Connected state)
        if (data.callInformation.initialContactId) {
          contactIdRef.current = data.callInformation.initialContactId;
        }
        // Start metrics polling when Connected and contactId available (handleCallInformation in App.svelte)
        if (agentStateRef.current === AgentState.Connected && contactIdRef.current) {
          connection.startMetricsPolling(contactIdRef.current);
        }
      }
      if (data.agentStates) {
        dispatch({ type: 'SET_AGENT_PRESENCE', payload: data.agentStates });
      }
      if (data.agentConnection) {
        dispatch({ type: 'SET_AGENT_MUTED', payload: data.agentConnection.muted });
      }
      if (data.offeredConference) {
        dispatch({ type: 'SET_OFFERED_CALL', payload: data.offeredConference.call });
      }
      if (data.previewCallInformation) {
        dispatch({ type: 'SET_PENDING_PREVIEW_CALL', payload: data.previewCallInformation.call });
      }
      if (data.stateChangeFailed) {
        console.warn('State change failed:', data.stateChangeFailed.rejectedState);
      }
      if (data.callFailed) {
        console.warn('Call failed for address:', data.callFailed.address);
      }
    });

    connection.on('closed', () => {
      dispatch({ type: 'SET_CONNECTED', payload: false });
    });

    return () => {
      connection.stopMetricsPolling();
      connection.disconnect();
      socketRef.current = null;
    };
  }, []);

  const manualOutboundEnabled = assignedGroup.configuredGroup?.enableManualOutboundWhenOffline || false;
  const allowAgentLeaveCall = appConfig.extensions.predictive?.allowAgentLeaveCall ?? false;
  const canPauseCallRecordings = Boolean(appConfig.extensions.pauseRecordings);
  const hasAmplifonSipFormFields = Boolean(appConfig.extensions.amplifonSipFormFields);
  const hasHCFSipFormFields = Boolean(appConfig.extensions.HCFSipFormFields);

  const context: Props = {
    state,
    dispatch,
    socketRef,
    manualOutboundEnabled,
    allowAgentLeaveCall,
    canPauseCallRecordings,
    hasAmplifonSipFormFields,
    hasHCFSipFormFields,
    loadTimezones,
  };
  return <AsteriskDiallerContext.Provider value={context}>{children}</AsteriskDiallerContext.Provider>;
};

export default AsteriskDiallerProvider;
